import React, { useState, useEffect } from 'react';
import { customersAPI, machinesAPI } from '../api/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { PlusCircle, Edit, Trash2, List, Download, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useSortableTable, SortableTh } from '../utils/useSortableTable';
import { normalizeGreek } from '../utils/greekUtils';
import { verifyAdminPassword } from '../utils/passwordUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const CustomersPage = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState(null); // null, 'add', 'edit', 'delete'
  
  // Search & Sort
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOption, setSortOption] = useState('Α-Ω');
  
  // Expander
  const [expanderOpen, setExpanderOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState('---');
  const [customerMachines, setCustomerMachines] = useState([]);
  const [customerStats, setCustomerStats] = useState(null);
  const [statsYear, setStatsYear] = useState(new Date().getFullYear());
  
  // Form data
  const [formData, setFormData] = useState({
    company_name: '',
    megasoft_code: '',
    afm: '',
    phone: '',
    mobile: '',
    email: '',
    address: '',
    install_location: '',
    contact_person: '',
    notes: ''
  });
  
  const [editTarget, setEditTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const { sortConfig, handleSort, sortData } = useSortableTable();

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      const response = await customersAPI.getAll();
      setCustomers(response.data);
    } catch (error) {
      toast.error('Σφάλμα φόρτωσης πελατών');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      company_name: '',
      megasoft_code: '',
      afm: '',
      phone: '',
      email: '',
      address: '',
      install_location: '',
      contact_person: ''
    });
    setAdminPassword('');
    setEditTarget(null);
    setDeleteTarget('');
  };

  const handleModeChange = (newMode) => {
    setMode(newMode);
    resetForm();
  };

  // ADD
  const handleAdd = async (e) => {
    e.preventDefault();
    
    if (!formData.company_name || !formData.megasoft_code) {
      toast.error('ΕΠΩΝΥΜΙΑ ΚΑΙ ID ΕΙΝΑΙ ΥΠΟΧΡΕΩΤΙΚΑ!');
      return;
    }
    
    if (formData.afm.trim() && (!/^\d+$/.test(formData.afm.trim()) || formData.afm.trim().length !== 9)) {
      toast.error('⚠️ ΤΟ ΑΦΜ ΠΡΕΠΕΙ ΝΑ ΑΠΟΤΕΛΕΙΤΑΙ ΑΠΟ ΑΚΡΙΒΩΣ 9 ΨΗΦΙΑ!');
      return;
    }
    
    try {
      await customersAPI.create(formData);
      toast.success('ΑΠΟΘΗΚΕΥΤΗΚΕ!');
      fetchCustomers();
      setMode(null);
      resetForm();
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error('Ο ΚΩΔΙΚΟΣ ΠΕΛΑΤΗ ΥΠΑΡΧΕΙ ΗΔΗ!');
      } else {
        toast.error('Σφάλμα αποθήκευσης');
      }
    }
  };

  // EDIT
  const handleEditSelect = (customerName) => {
    const customer = customers.find(c => c.company_name === customerName);
    if (customer) {
      setEditTarget(customer);
      setFormData(customer);
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    
    const isValid = await verifyAdminPassword(adminPassword);
    if (!isValid) {
      toast.error('ΛΑΘΟΣ ΚΩΔΙΚΟΣ');
      return;
    }
    
    if (formData.afm.trim() && (!/^\d+$/.test(formData.afm.trim()) || formData.afm.trim().length !== 9)) {
      toast.error('⚠️ ΤΟ ΑΦΜ ΠΡΕΠΕΙ ΝΑ ΑΠΟΤΕΛΕΙΤΑΙ ΑΠΟ ΑΚΡΙΒΩΣ 9 ΨΗΦΙΑ!');
      return;
    }
    
    try {
      await customersAPI.update(editTarget.id, formData);
      toast.success('ΕΝΗΜΕΡΩΘΗΚΕ!');
      fetchCustomers();
      setMode(null);
      resetForm();
    } catch (error) {
      toast.error('Σφάλμα ενημέρωσης');
    }
  };

  // DELETE
  const handleDelete = async (e) => {
    e.preventDefault();
    
    const isValid = await verifyAdminPassword(adminPassword);
    if (!isValid) {
      toast.error('ΛΑΘΟΣ ΚΩΔΙΚΟΣ');
      return;
    }
    
    const customer = customers.find(c => c.company_name === deleteTarget);
    if (!customer) return;
    
    try {
      // Check if customer has machines
      const token = localStorage.getItem('token');
      const machinesRes = await axios.get(`${API_URL}/machines`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const customerMachines = machinesRes.data.filter(m => m.customer_id === customer.id);
      
      if (customerMachines.length > 0) {
        toast.error(`ΣΦΑΛΜΑ: Ο ΠΕΛΑΤΗΣ ΕΧΕΙ ${customerMachines.length} ΜΗΧΑΝΗΜΑΤΑ. ΔΙΑΓΡΑΨΤΕ ΠΡΩΤΑ ΤΑ ΜΗΧΑΝΗΜΑΤΑ.`);
        return;
      }
      
      await customersAPI.delete(customer.id);
      toast.warning('ΔΙΑΓΡΑΦΗΚΕ.');
      fetchCustomers();
      setMode(null);
      resetForm();
    } catch (error) {
      toast.error('Σφάλμα διαγραφής');
    }
  };

  // Filter & Sort
  const getFilteredAndSortedCustomers = () => {
    const normSearch = normalizeGreek(searchTerm).toLowerCase();
    let filtered = customers.filter(c => 
      normalizeGreek(c.company_name).toLowerCase().includes(normSearch) ||
      c.megasoft_code.toLowerCase().includes(searchTerm.toLowerCase())
    );
    
    if (sortOption === 'Α-Ω') {
      filtered.sort((a, b) => normalizeGreek(a.company_name).localeCompare(normalizeGreek(b.company_name), 'el'));
    } else if (sortOption === 'Ω-Α') {
      filtered.sort((a, b) => normalizeGreek(b.company_name).localeCompare(normalizeGreek(a.company_name), 'el'));
    } else {
      filtered.sort((a, b) => a.megasoft_code.localeCompare(b.megasoft_code));
    }
    
    return filtered;
  };

  // Excel Export
  const handleExcelExport = () => {
    const filtered = getFilteredAndSortedCustomers();
    const csvContent = [
      ['ΚΩΔΙΚΟΣ ΠΕΛΑΤΗ', 'Επωνυμία', 'ΑΦΜ', 'Τηλέφωνο', 'Email', 'Διεύθυνση', 'Σημείο Εγκατάστασης', 'Υπεύθυνος'],
      ...filtered.map(c => [
        c.megasoft_code,
        c.company_name,
        c.afm || '',
        c.phone || '',
        c.email || '',
        c.address || '',
        c.install_location || '',
        c.contact_person || ''
      ])
    ].map(row => row.join('\t')).join('\n');
    
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'customers_list.csv';
    link.click();
  };

  // Fetch machines and stats for selected customer
  const fetchCustomerMachines = async (customerName) => {
    if (customerName === '---') {
      setCustomerMachines([]);
      setCustomerStats(null);
      return;
    }
    
    const customer = getFilteredAndSortedCustomers().find(c => c.company_name === customerName);
    if (!customer) return;
    
    try {
      const token = localStorage.getItem('token');
      
      // Fetch ALL machines (active + inactive)
      const machinesRes = await axios.get(`${API_URL}/customers/${customer.id}/machines`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomerMachines(machinesRes.data);
      
      // Fetch stats for the selected year
      const statsRes = await axios.get(`${API_URL}/customers/${customer.id}/stats/${statsYear}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomerStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching customer data:', error);
      setCustomerMachines([]);
      setCustomerStats(null);
    }
  };

  useEffect(() => {
    if (selectedCustomer !== '---') {
      fetchCustomerMachines(selectedCustomer);
    }
  }, [selectedCustomer, statsYear]);

  if (loading) {
    return <div className="flex justify-center py-12">Φόρτωση...</div>;
  }

  const filteredCustomers = getFilteredAndSortedCustomers();

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl sm:text-3xl font-bold text-gray-900">ΔΙΑΧΕΙΡΙΣΗ ΠΕΛΑΤΩΝ</h1>
      </div>

      {/* Mode Buttons */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Button 
          onClick={() => handleModeChange('add')}
          className="bg-green-600 hover:bg-green-700 text-xs sm:text-sm"
        >
          <PlusCircle className="h-4 w-4 mr-1 sm:mr-2 flex-shrink-0" />
          <span className="truncate">ΝΕΑ ΕΓΓΡΑΦΗ</span>
        </Button>
        <Button 
          onClick={() => handleModeChange('edit')}
          className="bg-blue-600 hover:bg-blue-700 text-xs sm:text-sm"
        >
          <Edit className="h-4 w-4 mr-1 sm:mr-2 flex-shrink-0" />
          <span className="truncate">ΔΙΟΡΘΩΣΗ</span>
        </Button>
        <Button 
          onClick={() => handleModeChange('delete')}
          className="bg-red-600 hover:bg-red-700 text-xs sm:text-sm"
        >
          <Trash2 className="h-4 w-4 mr-1 sm:mr-2 flex-shrink-0" />
          <span className="truncate">ΔΙΑΓΡΑΦΗ</span>
        </Button>
        <Button 
          onClick={() => handleModeChange(null)}
          variant="outline"
          className="text-xs sm:text-sm"
        >
          <List className="h-4 w-4 mr-1 sm:mr-2 flex-shrink-0" />
          <span className="truncate">ΛΙΣΤΑ / Reset</span>
        </Button>
      </div>

      <hr className="border-gray-300" />

      {/* ADD MODE */}
      {mode === 'add' && (
        <Card>
          <CardHeader>
            <CardTitle>🆕 ΠΡΟΣΘΗΚΗ ΝΕΟΥ ΠΕΛΑΤΗ</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleAdd} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">ΕΠΩΝΥΜΙΑ ΕΤΑΙΡΕΙΑΣ *</label>
                  <Input
                    value={formData.company_name}
                    onChange={(e) => setFormData({...formData, company_name: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">ΚΩΔΙΚΟΣ ΠΕΛΑΤΗ *</label>
                  <Input
                    value={formData.megasoft_code}
                    onChange={(e) => setFormData({...formData, megasoft_code: e.target.value})}
                    required
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">ΑΦΜ</label>
                  <Input
                    value={formData.afm}
                    onChange={(e) => setFormData({...formData, afm: e.target.value})}
                    placeholder="9 ψηφία"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">ΤΗΛΕΦΩΝΟ</label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  />
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium">ΥΠΕΥΘΥΝΟΣ ΕΠΙΚΟΙΝΩΝΙΑΣ</label>
                <Input
                  value={formData.contact_person}
                  onChange={(e) => setFormData({...formData, contact_person: e.target.value})}
                />
              </div>
              
              <div>
                <label className="text-sm font-medium">ΤΗΛΕΦΩΝΟ</label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                />
              </div>
              
              <div>
                <label className="text-sm font-medium">ΚΙΝΗΤΟ</label>
                <Input
                  value={formData.mobile}
                  onChange={(e) => setFormData({...formData, mobile: e.target.value})}
                  placeholder="Κινητό τηλέφωνο"
                />
              </div>
              
              <div>
                <label className="text-sm font-medium">Email</label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                />
              </div>
              
              <div>
                <label className="text-sm font-medium">ΔΙΕΥΘΥΝΣΗ</label>
                <Input
                  value={formData.address}
                  onChange={(e) => setFormData({...formData, address: e.target.value})}
                />
              </div>
              
              <div>
                <label className="text-sm font-medium">ΣΗΜΕΙΟ ΕΓΚΑΤΑΣΤΑΣΗΣ</label>
                <Input
                  value={formData.install_location}
                  onChange={(e) => setFormData({...formData, install_location: e.target.value})}
                />
              </div>
              
              <div className="col-span-2">
                <label className="text-sm font-medium">ΣΧΟΛΙΑ</label>
                <textarea
                  className="w-full px-3 py-2 border border-gray-300 rounded-md min-h-[80px]"
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  placeholder="Προσθέστε σχόλια ή σημειώσεις..."
                />
              </div>
              
              <Button type="submit" className="bg-[#004a99] hover:bg-[#003a7a]">
                ΑΠΟΘΗΚΕΥΣΗ
              </Button>
            </form>
          </CardContent>
        </Card>
      )}

      {/* EDIT MODE */}
      {mode === 'edit' && customers.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>✏️ ΕΠΕΞΕΡΓΑΣΙΑ ΣΤΟΙΧΕΙΩΝ</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium">ΕΠΙΛΕΞΤΕ ΠΕΛΑΤΗ</label>
                <select
                  onChange={(e) => handleEditSelect(e.target.value)}
                  className="w-full h-10 px-3 border border-gray-300 rounded-md"
                >
                  <option value="">---</option>
                  {customers.map(c => (
                    <option key={c.id} value={c.company_name}>{c.company_name}</option>
                  ))}
                </select>
              </div>
              
              {editTarget && (
                <form onSubmit={handleEdit} className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium">ΝΕΑ ΕΠΩΝΥΜΙΑ</label>
                      <Input
                        value={formData.company_name}
                        onChange={(e) => setFormData({...formData, company_name: e.target.value})}
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium">ΚΩΔΙΚΟΣ ΠΕΛΑΤΗ</label>
                      <Input
                        value={formData.megasoft_code}
                        onChange={(e) => setFormData({...formData, megasoft_code: e.target.value})}
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium">ΑΦΜ</label>
                    <Input
                      value={formData.afm}
                      onChange={(e) => setFormData({...formData, afm: e.target.value})}
                    />
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium">ΤΗΛΕΦΩΝΟ</label>
                    <Input
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    />
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium">ΚΙΝΗΤΟ</label>
                    <Input
                      value={formData.mobile}
                      onChange={(e) => setFormData({...formData, mobile: e.target.value})}
                      placeholder="Κινητό τηλέφωνο"
                    />
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium">Email</label>
                    <Input
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                    />
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium">ΔΙΕΥΘΥΝΣΗ</label>
                    <Input
                      value={formData.address}
                      onChange={(e) => setFormData({...formData, address: e.target.value})}
                    />
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium">ΣΗΜΕΙΟ ΕΓΚΑΤΑΣΤΑΣΗΣ</label>
                    <Input
                      value={formData.install_location}
                      onChange={(e) => setFormData({...formData, install_location: e.target.value})}
                    />
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium">ΥΠΕΥΘΥΝΟΣ ΕΠΙΚΟΙΝΩΝΙΑΣ</label>
                    <Input
                      value={formData.contact_person}
                      onChange={(e) => setFormData({...formData, contact_person: e.target.value})}
                    />
                  </div>
                  
                  <div className="col-span-2">
                    <label className="text-sm font-medium">ΣΧΟΛΙΑ</label>
                    <textarea
                      className="w-full px-3 py-2 border border-gray-300 rounded-md min-h-[80px]"
                      value={formData.notes}
                      onChange={(e) => setFormData({...formData, notes: e.target.value})}
                      placeholder="Προσθέστε σχόλια ή σημειώσεις..."
                    />
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium">ΚΩΔΙΚΟΣ Admin *</label>
                    <Input
                      type="password"
                      value={adminPassword}
                      onChange={(e) => setAdminPassword(e.target.value)}
                      required
                    />
                  </div>
                  
                  <Button type="submit" className="bg-[#004a99] hover:bg-[#003a7a]">
                    ΕΝΗΜΕΡΩΣΗ
                  </Button>
                </form>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* DELETE MODE */}
      {mode === 'delete' && customers.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>🗑️ ΔΙΑΓΡΑΦΗ ΠΕΛΑΤΗ</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleDelete} className="space-y-4">
              <div>
                <label className="text-sm font-medium">ΠΡΟΣ ΔΙΑΓΡΑΦΗ</label>
                <select
                  value={deleteTarget}
                  onChange={(e) => setDeleteTarget(e.target.value)}
                  className="w-full h-10 px-3 border border-gray-300 rounded-md"
                  required
                >
                  <option value="">---</option>
                  {customers.map(c => (
                    <option key={c.id} value={c.company_name}>{c.company_name}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="text-sm font-medium">ΚΩΔΙΚΟΣ Admin *</label>
                <Input
                  type="password"
                  value={adminPassword}
                  onChange={(e) => setAdminPassword(e.target.value)}
                  required
                />
              </div>
              
              <Button type="submit" className="bg-red-600 hover:bg-red-700">
                ⚠️ ΟΡΙΣΤΙΚΗ ΔΙΑΓΡΑΦΗ
              </Button>
            </form>
          </CardContent>
        </Card>
      )}

      {/* LIST VIEW (Default) */}
      {!mode && (
        <>
          {customers.length > 0 ? (
            <>
              {/* Search & Sort */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="col-span-2">
                  <label className="text-sm font-medium">🔍 ΑΝΑΖΗΤΗΣΗ (ΕΠΩΝΥΜΙΑ Η ID)</label>
                  <Input
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Αναζήτηση..."
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">🔃 ΤΑΞΙΝΟΜΗΣΗ</label>
                  <select
                    value={sortOption}
                    onChange={(e) => setSortOption(e.target.value)}
                    className="w-full h-10 px-3 border border-gray-300 rounded-md"
                  >
                    <option>Α-Ω</option>
                    <option>Ω-Α</option>
                    <option>ΚΩΔΙΚΟΣ ΠΕΛΑΤΗ</option>
                  </select>
                </div>
              </div>

              {/* Table */}
              <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-[#004a99] text-white">
                      <tr>
                        <SortableTh columnKey="id" sortConfig={sortConfig} onSort={handleSort} className="px-3 py-2 text-left text-xs font-semibold">ID</SortableTh>
                        <SortableTh columnKey="megasoft_code" sortConfig={sortConfig} onSort={handleSort} className="px-3 py-2 text-left text-xs font-semibold">ΚΩΔ.</SortableTh>
                        <SortableTh columnKey="company_name" sortConfig={sortConfig} onSort={handleSort} className="px-3 py-2 text-left text-xs font-semibold">ΕΠΩΝΥΜΙΑ</SortableTh>
                        <SortableTh columnKey="phone" sortConfig={sortConfig} onSort={handleSort} className="px-3 py-2 text-left text-xs font-semibold">ΤΗΛΕΦΩΝΟ</SortableTh>
                        <SortableTh columnKey="mobile" sortConfig={sortConfig} onSort={handleSort} className="px-3 py-2 text-left text-xs font-semibold">ΚΙΝΗΤΟ</SortableTh>
                        <SortableTh columnKey="contact_person" sortConfig={sortConfig} onSort={handleSort} className="px-3 py-2 text-left text-xs font-semibold">ΥΠΕΥΘΥΝΟΣ</SortableTh>
                        <SortableTh columnKey="email" sortConfig={sortConfig} onSort={handleSort} className="px-3 py-2 text-left text-xs font-semibold">EMAIL</SortableTh>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {sortData(filteredCustomers).map((customer) => (
                        <tr key={customer.id} className="hover:bg-gray-50">
                          <td className="px-3 py-2 text-xs font-mono text-gray-500">{customer.id}</td>
                          <td className="px-3 py-2 text-xs font-mono">{customer.megasoft_code}</td>
                          <td className="px-3 py-2 text-xs font-medium">{customer.company_name}</td>
                          <td className="px-3 py-2 text-xs">{customer.phone || '-'}</td>
                          <td className="px-3 py-2 text-xs">{customer.mobile || '-'}</td>
                          <td className="px-3 py-2 text-xs">{customer.contact_person || '-'}</td>
                          <td className="px-3 py-2 text-xs">{customer.email || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Excel Export */}
              <Button onClick={handleExcelExport} variant="outline" className="gap-2">
                <Download className="h-4 w-4" />
                ΕΞΑΓΩΓΗ ΛΙΣΤΑΣ ΣΕ Excel
              </Button>

              {/* Expander */}
              <Card>
                <CardHeader 
                  className="cursor-pointer hover:bg-gray-50"
                  onClick={() => setExpanderOpen(!expanderOpen)}
                >
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">
                      🔍 ΑΝΑΛΥΤΙΚΕΣ ΛΕΠΤΟΜΕΡΕΙΕΣ & ΜΗΧΑΝΗΜΑΤΑ
                    </CardTitle>
                    {expanderOpen ? <ChevronUp /> : <ChevronDown />}
                  </div>
                </CardHeader>
                {expanderOpen && (
                  <CardContent className="space-y-4">
                    <div>
                      <label className="text-sm font-medium">ΕΠΙΛΕΞΤΕ ΠΕΛΑΤΗ ΓΙΑ ΠΡΟΒΟΛΗ</label>
                      <select
                        value={selectedCustomer}
                        onChange={(e) => setSelectedCustomer(e.target.value)}
                        className="w-full h-10 px-3 border border-gray-300 rounded-md"
                      >
                        <option>---</option>
                        {filteredCustomers.map(c => (
                          <option key={c.id} value={c.company_name}>{c.company_name}</option>
                        ))}
                      </select>
                    </div>
                    
                    {selectedCustomer !== '---' && (() => {
                      const customer = filteredCustomers.find(c => c.company_name === selectedCustomer);
                      if (!customer) return null;
                      
                      // Calculate stats
                      const activeMachines = customerMachines.filter(m => m.is_active === 1 || m.is_active === true);
                      const inactiveMachines = customerMachines.filter(m => m.is_active === 0 || m.is_active === false);
                      const totalMonthlyFee = activeMachines.reduce((sum, m) => sum + (parseFloat(m.price_per_month) || 0), 0);
                      
                      return (
                        <div className="space-y-6">
                          {/* Customer Header */}
                          <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-4 rounded-lg border-l-4 border-blue-500">
                            <h3 className="text-2xl font-bold text-blue-900">{customer.company_name}</h3>
                            <p className="text-sm text-blue-700">Κωδ. Πελάτη: {customer.megasoft_code} | ΑΦΜ: {customer.afm || '-'}</p>
                          </div>
                          
                          {/* Stats Cards */}
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                            <Card className="border-l-4 border-l-green-500">
                              <CardContent className="pt-6">
                                <div className="text-center">
                                  <p className="text-3xl font-bold text-green-600">{activeMachines.length}</p>
                                  <p className="text-sm text-gray-600">Ενεργά Μηχανήματα</p>
                                </div>
                              </CardContent>
                            </Card>
                            
                            <Card className="border-l-4 border-l-orange-500">
                              <CardContent className="pt-6">
                                <div className="text-center">
                                  <p className="text-3xl font-bold text-orange-600">{inactiveMachines.length}</p>
                                  <p className="text-sm text-gray-600">Σε Παύση</p>
                                </div>
                              </CardContent>
                            </Card>
                            
                            <Card className="border-l-4 border-l-blue-500">
                              <CardContent className="pt-6">
                                <div className="text-center">
                                  <p className="text-3xl font-bold text-blue-600">{totalMonthlyFee.toFixed(2)}€</p>
                                  <p className="text-sm text-gray-600">Σύνολο Παγίων/Μήνα</p>
                                </div>
                              </CardContent>
                            </Card>
                          </div>
                          
                          {/* Contact Info */}
                          <Card>
                            <CardHeader>
                              <CardTitle className="text-lg">📞 ΣΤΟΙΧΕΙΑ ΕΠΙΚΟΙΝΩΝΙΑΣ</CardTitle>
                            </CardHeader>
                            <CardContent>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                  <p><strong>👤 ΥΠΕΥΘΥΝΟΣ:</strong> {customer.contact_person || '-'}</p>
                                  <p><strong>📞 ΤΗΛΕΦΩΝΟ:</strong> {customer.phone || '-'}</p>
                                  <p><strong>📧 Email:</strong> {customer.email || '-'}</p>
                                </div>
                                <div className="space-y-2">
                                  <p><strong>📍 ΔΙΕΥΘΥΝΣΗ:</strong> {customer.address || '-'}</p>
                                  <p><strong>🏢 ΤΟΠΟΘΕΣΙΑ:</strong> {customer.install_location || '-'}</p>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                          
                          {/* Yearly Stats Summary */}
                          {customerStats && (
                            <Card className="border-l-4 border-l-purple-500">
                              <CardHeader>
                                <div className="flex items-center justify-between">
                                  <CardTitle className="text-lg">📊 ΣΥΓΚΕΝΤΡΩΤΙΚΑ ΣΤΟΙΧΕΙΑ ΕΤΟΥΣ</CardTitle>
                                  <select
                                    value={statsYear}
                                    onChange={(e) => setStatsYear(parseInt(e.target.value))}
                                    className="px-3 py-1 border rounded text-sm"
                                  >
                                    {[2024, 2025, 2026, 2027].map(y => (
                                      <option key={y} value={y}>{y}</option>
                                    ))}
                                  </select>
                                </div>
                              </CardHeader>
                              <CardContent>
                                {/* Summary Cards */}
                                <div className="grid grid-cols-4 gap-4 mb-4">
                                  <Card className="bg-blue-50">
                                    <CardContent className="pt-4 text-center">
                                      <p className="text-2xl font-bold text-blue-600">
                                        {customerStats.total_pages.toLocaleString()}
                                      </p>
                                      <p className="text-xs text-gray-600">ΣΥΝΟΛΟ ΣΕΛΙΔΩΝ</p>
                                    </CardContent>
                                  </Card>
                                  
                                  <Card className="bg-gray-50">
                                    <CardContent className="pt-4 text-center">
                                      <p className="text-2xl font-bold text-gray-700">
                                        {customerStats.total_black.toLocaleString()}
                                      </p>
                                      <p className="text-xs text-gray-600">Μ/Λ (BLACK)</p>
                                    </CardContent>
                                  </Card>
                                  
                                  <Card className="bg-pink-50">
                                    <CardContent className="pt-4 text-center">
                                      <p className="text-2xl font-bold text-pink-600">
                                        {customerStats.total_color.toLocaleString()}
                                      </p>
                                      <p className="text-xs text-gray-600">ΕΓΧΡΩΜΕΣ</p>
                                    </CardContent>
                                  </Card>
                                  
                                  <Card className="bg-orange-50">
                                    <CardContent className="pt-4 text-center">
                                      <p className="text-2xl font-bold text-orange-600">
                                        {customerStats.service_count}
                                      </p>
                                      <p className="text-xs text-gray-600">SERVICE ({statsYear})</p>
                                    </CardContent>
                                  </Card>
                                </div>
                                
                                {/* Machine Breakdown Table */}
                                {customerStats.machine_breakdown.length > 0 && (
                                  <div className="overflow-x-auto">
                                    <table className="w-full text-sm border-collapse">
                                      <thead className="bg-purple-100">
                                        <tr>
                                          <th className="border px-3 py-2 text-left">ΜΗΧΑΝΗΜΑ</th>
                                          <th className="border px-3 py-2 text-center">Μ/Λ</th>
                                          <th className="border px-3 py-2 text-center">ΕΓΧΡ</th>
                                          <th className="border px-3 py-2 text-center">A3</th>
                                          <th className="border px-3 py-2 text-center">A3 ΧΡ</th>
                                          <th className="border px-3 py-2 text-center">ΣΥΝΟΛΟ</th>
                                          <th className="border px-3 py-2 text-center">ΜΕΤΡΗΣΕΙΣ</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {customerStats.machine_breakdown.map((m, idx) => (
                                          <tr key={idx} className="hover:bg-gray-50">
                                            <td className="border px-3 py-2">
                                              <div>
                                                <p className="font-semibold">{m.brand} {m.model}</p>
                                                <p className="text-xs text-gray-600 font-mono">S/N: {m.machine_serial}</p>
                                              </div>
                                            </td>
                                            <td className="border px-3 py-2 text-center">{m.total_black.toLocaleString()}</td>
                                            <td className="border px-3 py-2 text-center">{m.total_color.toLocaleString()}</td>
                                            <td className="border px-3 py-2 text-center">{m.total_a3.toLocaleString()}</td>
                                            <td className="border px-3 py-2 text-center">{m.total_a3_color.toLocaleString()}</td>
                                            <td className="border px-3 py-2 text-center font-bold text-blue-600">
                                              {m.total_pages.toLocaleString()}
                                            </td>
                                            <td className="border px-3 py-2 text-center">{m.readings_count}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                      <tfoot className="bg-purple-50 font-bold">
                                        <tr>
                                          <td className="border px-3 py-2">ΣΥΝΟΛΟ</td>
                                          <td className="border px-3 py-2 text-center">{customerStats.total_black.toLocaleString()}</td>
                                          <td className="border px-3 py-2 text-center">{customerStats.total_color.toLocaleString()}</td>
                                          <td className="border px-3 py-2 text-center">-</td>
                                          <td className="border px-3 py-2 text-center">-</td>
                                          <td className="border px-3 py-2 text-center text-blue-600 text-lg">
                                            {customerStats.total_pages.toLocaleString()}
                                          </td>
                                          <td className="border px-3 py-2 text-center">-</td>
                                        </tr>
                                      </tfoot>
                                    </table>
                                  </div>
                                )}
                              </CardContent>
                            </Card>
                          )}
                          
                          {/* Active Machines */}
                          {activeMachines.length > 0 && (
                            <Card>
                              <CardHeader>
                                <CardTitle className="text-lg">✅ ΕΝΕΡΓΑ ΜΗΧΑΝΗΜΑΤΑ ({activeMachines.length})</CardTitle>
                              </CardHeader>
                              <CardContent>
                                <div className="overflow-x-auto">
                                  <table className="w-full text-sm border-collapse">
                                    <thead className="bg-green-100">
                                      <tr>
                                        <th className="border px-3 py-2 text-left">S/N</th>
                                        <th className="border px-3 py-2 text-left">ΜΑΡΚΑ</th>
                                        <th className="border px-3 py-2 text-left">ΜΟΝΤΕΛΟ</th>
                                        <th className="border px-3 py-2 text-right">ΠΑΓΙΟ €</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {activeMachines.map((m, idx) => (
                                        <tr key={idx} className="hover:bg-gray-50">
                                          <td className="border px-3 py-2 font-mono">{m.machine_serial}</td>
                                          <td className="border px-3 py-2">{m.brand}</td>
                                          <td className="border px-3 py-2">{m.model}</td>
                                          <td className="border px-3 py-2 text-right">{(m.price_per_month || 0).toFixed(2)}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </CardContent>
                            </Card>
                          )}
                          
                          {/* Inactive Machines */}
                          {inactiveMachines.length > 0 && (
                            <Card>
                              <CardHeader>
                                <CardTitle className="text-lg">⏸️ ΜΗΧΑΝΗΜΑΤΑ ΣΕ ΠΑΥΣΗ ({inactiveMachines.length})</CardTitle>
                              </CardHeader>
                              <CardContent>
                                <div className="space-y-2">
                                  {inactiveMachines.map((m, idx) => (
                                    <div key={idx} className="p-3 bg-orange-50 rounded border border-orange-200">
                                      <div className="flex justify-between items-center">
                                        <div>
                                          <p className="font-semibold">{m.brand} {m.model}</p>
                                          <p className="text-xs text-gray-600">S/N: {m.machine_serial}</p>
                                        </div>
                                        <div className="text-right text-xs">
                                          {m.deactivated_date && <p className="text-orange-600">Από: {m.deactivated_date}</p>}
                                          {m.deactivation_reason && <p className="text-gray-600">{m.deactivation_reason}</p>}
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </CardContent>
                            </Card>
                          )}
                        </div>
                      );
                    })()}
                  </CardContent>
                )}
              </Card>
            </>
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <p className="text-gray-600">
                  ℹ️ Η ΒΑΣΗ ΔΕΔΟΜΕΝΩΝ ΕΙΝΑΙ ΚΕΝΗ. ΠΡΟΣΘΕΣΤΕ ΤΟΝ ΠΡΩΤΟ ΣΑΣ ΠΕΛΑΤΗ!
                </p>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
};

export default CustomersPage;
